import { Signal } from "signals";
import { HomeCommand } from "slotworks/commands/betbar/home-command";
import AutoplaySection from "slotworks/components/betbar/menus/auto-play-section";
import BetSection from "slotworks/components/betbar/menus/bet-section";
import SettingsSection from "slotworks/components/betbar/menus/general-section";
import HistorySection from "slotworks/components/betbar/menus/history-section";
import { MenuSection } from "slotworks/components/betbar/menus/menu-section";
import AutoplayOptions from "slotworks/components/betbar/vo/autoplay-options";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import GeneralOptions from "slotworks/components/betbar/vo/general-options";
import Components from "slotworks/components/components";
import SoundToggleComponent from "slotworks/components/sound/sound-toggle-component";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import Layers from "slotworks/graphics/layers/layers";
import Currency from "slotworks/i18n/currency";
import SettingsModel from "slotworks/model/settings/settings-model";
import autoplayModel from "slotworks/model/autoplay-model";

export default class BetMenuComponent {

    public static AUTOPLAY: string = "autoplay";
    public static PAYTABLE: string = "paytable";
    public static BET: string = "bet";
    public static RULES: string = "rules";
    public static HISTORY: string = "history";
    public static SETTINGS: string = "settings";

    public static sceneAliases: Map<string, string> = new Map<string, string>();

    public onVisibleChange: Signal;
    public onAutoplayOptionsCommitted: Signal;
    public onBetOptionsCommitted: Signal;
    public onSettingsOptionsCommitted: Signal;

    private onBetOptionsChanged: Signal;
    private hasNewBetOptions: boolean;

    private autoplayOptions: AutoplayOptions;
    private betOptions: BetOptions;
    private generalOptions: GeneralOptions;

    private currentSection: MenuSection;

    private currentScene: string;
    private open: boolean;

    private defaultTab: string[];

    private actions: Map<string, () => void> = new Map<string, () => void>();

    private buttons: Map<string, FlexiButton> = new Map<string, FlexiButton>();

    private soundToggle: SoundToggleComponent;

    private homeButton: FlexiButton;

    private balanceText: FlexiText;
    private stakeText: FlexiText;
    private winText: FlexiText;

    private totalWin: number = 0;
    private balance: number = 0;

    private homeURL: string;

    // TODO: This whole class seems overly difficult to read now, we can probably make it much simpler
    constructor(defaultTab: string[]) {
        this.defaultTab = defaultTab;

        this.onAutoplayOptionsCommitted = new Signal();
        this.onAutoplayOptionsCommitted.add((options: AutoplayOptions) => this.updateAutoPlayOptions(options));

        this.onBetOptionsCommitted = new Signal();

        this.onBetOptionsChanged = new Signal();
        this.onBetOptionsChanged.add(this.onBetOptionsUpdatedHandler);

        this.onSettingsOptionsCommitted = new Signal();
        this.onSettingsOptionsCommitted.add((options: GeneralOptions) => this.setGeneralOptions(options));

        this.onVisibleChange = new Signal();
    }

    public show(tabName?: string) {
        this.open = true;

        Layers.Menus.setScene("betMenu").execute();
        this.setup();

        if (this.actions.size === 0) {
            if(!autoplayModel.autoplayDisabled) {
                this.setupAction(BetMenuComponent.AUTOPLAY, this.showAutoPlayOptions);
            }
            this.setupAction(BetMenuComponent.BET, this.showBetOptions);
            this.setupAction(BetMenuComponent.SETTINGS, this.showSettings);
            this.setupAction(BetMenuComponent.PAYTABLE, this.showPaytable);
            this.setupAction(BetMenuComponent.RULES, this.showRules);
            if(SettingsModel.historymenu) {
                this.setupAction(BetMenuComponent.HISTORY, this.showHistory);
            } else {
                const historyButtonDiv = document.querySelector("#betmenu .betmenu__buttons #history");
                if(historyButtonDiv) {
                    historyButtonDiv.remove();
                }
            }
        }

        this.setupButtons(true);

        if (tabName) {
            if (this.actions.has(tabName)) {
                this.actions.get(tabName)();
            }
        } else {
            this.showDefaultTab();
        }

        this.onVisibleChange.dispatch(true);

        // Blocking background
        const background = Layers.Menus.getShape("background");
        if (background) {
            background.interactive = true;
        }
    }

    public hide = () => {
        const hasChanged = this.open === true;
        this.open = false;

        this.showContent("default");

        this.setupButtons(false);
        this.destroy();
        Layers.Menus.defaultScene().execute();

        if (hasChanged) {
            this.onVisibleChange.dispatch(false);
        }

        if (this.hasNewBetOptions) {
            this.hasNewBetOptions = false;
            this.onBetOptionsCommitted.dispatch(this.betOptions);
        }

    }

    public setAutoplayOptions(options: AutoplayOptions) {
        this.autoplayOptions = options;
    }

    public setBetOptions(options: BetOptions) {
        this.betOptions = options;
        this.updateFields();
    }

    public setGeneralOptions(options: GeneralOptions) {
        this.generalOptions = options;
        this.updateFields();
    }

    public toggle = () => {
        this.open ? this.hide() : this.show();
    }

    public setTotalWin(value: number) {
        this.totalWin = value;
        this.updateFields();
    }

    public setBalance(value: number) {
        this.balance = value;
        this.updateFields();
    }

    public setHomeURL(url: string) {
        this.homeURL = url;
        this.homeButton = Layers.BetBar.getFlexiButton("home");
        if (this.homeButton) {
            if (this.homeURL === "close" || this.homeURL === "" || this.homeURL === undefined || this.homeURL === null)  {
                this.homeButton.setVisible(false);
            } else {
                this.homeButton.setVisible(true);
            }
        }
    }

    private onBetOptionsUpdatedHandler = (betOptions: BetOptions) => {
        this.hasNewBetOptions = true;
        this.setBetOptions(betOptions);
        this.onBetOptionsCommitted.dispatch(this.betOptions);
    }

    private showDefaultTab() {
        for (const value of this.defaultTab) {
            if (this.actions.has(value)) {
                this.actions.get(value)();
                break;
            }
        }
    }

    private setupAction(name: string, action: () => void) {
        if (this.hasTabAndTabContent(name)) {
            this.actions.set(name, action);
        }
    }

    private hasTabAndTabContent(name: string) {
        return Layers.Menus.getFlexiButton(name) && Layers.MenusContent.hasScene(this.getSceneAlias(name));
    }

    private homePressedHandler = () => {
        HomeCommand(this.homeURL).execute();
    }

    private updateAutoPlayOptions = (options: AutoplayOptions) => {
        const autoplays = options.autoplays;
        const lossLimitSelected = options.lossLimit > 0;

        this.setAutoplayOptions(options);

        let closeEnabled = autoplays === 0 || lossLimitSelected;
        if (!SettingsModel.isUKJurisdiction()) {
            closeEnabled = true;
        }

        this.enableClose(closeEnabled);
    }

    private updateFields() {
        if (this.betOptions && this.stakeText) {
            this.stakeText.setText(Currency.format(this.betOptions.getTotalStake()));
        }
        if (this.winText) {
            this.winText.setText(this.totalWin === 0 ? "" : Currency.format(this.totalWin));
        }
        if (this.balanceText) {
            this.balanceText.setText(Currency.format(this.balance));
        }
    }

    private showAutoPlayOptions = () => {
        this.showContent(BetMenuComponent.AUTOPLAY);

        this.currentSection = new AutoplaySection(this.onAutoplayOptionsCommitted, this.autoplayOptions);
    }

    private showBetOptions = () => {
        this.showContent(BetMenuComponent.BET);

        this.currentSection = new BetSection(this.onBetOptionsChanged, this.betOptions);
    }

    private showSettings = () => {
        this.showContent(BetMenuComponent.SETTINGS);

        this.currentSection = new SettingsSection(this.onSettingsOptionsCommitted, this.generalOptions);
    }

    private showPaytable = () => {
        this.showContent(BetMenuComponent.PAYTABLE);
    }

    private showRules = () => {
        this.showContent(BetMenuComponent.RULES);
    }

    private showHistory = () => {
        this.showContent(BetMenuComponent.HISTORY);

        this.currentSection = new HistorySection(null, null);
    }

    private showContent(scene: string) {
        this.enableClose(true);

        if (scene !== "default") {
            this.resetAutoPlays();
        }

        if (this.buttons.has(this.currentScene)) {
            this.buttons.get(this.currentScene).setSelected(false);
            this.buttons.get(this.currentScene).setEnabled(true);
        }
        if (this.currentSection) {
            this.currentSection.destroy();
            this.currentSection = null;
        }

        if (this.buttons.has(scene)) {
            this.buttons.get(scene).setSelected(true);
            this.buttons.get(scene).setEnabled(false);
        }

        this.currentScene = scene;

        Layers.MenusContent.setScene(this.getSceneAlias(scene)).execute();

        if (this.showingPaytable()) {
            Components.paytable.show();
        } else {
            Components.paytable.hide();
        }
    }

    private showingPaytable() {
        return this.currentScene === BetMenuComponent.PAYTABLE;
    }

    private resetAutoPlays() {
        if (this.autoplayOptions) {
            const lossLimitSelected = this.autoplayOptions.lossLimit > 0;

            if (!lossLimitSelected && SettingsModel.isUKJurisdiction()) {
                this.autoplayOptions.autoplays = 0;
                this.onAutoplayOptionsCommitted.dispatch(this.autoplayOptions);
            }
        }
    }

    private setupButtons(setup: boolean) {
        this.setupButton(Layers.Menus, "close", this.hide, setup);

        this.actions.forEach((action: () => void, name: string, original: Map<string, () => void>) => {
            this.setupButton(Layers.Menus, name, action, setup);
        });

        if(autoplayModel.autoplayDisabled) 
            this.hideAutoplayButton();
        if(!SettingsModel.historymenu) {
            this.hideHistoryButton();
        }
    }

    private setupButton(layer: Layers, id: string, listener: Function, on: boolean) {
        const button = layer.getFlexiButton(id);

        if (button) {
            if (on) {
                this.buttons.set(id, button);
                button.setSelected(false);
                button.setEnabled(true);
                button.on(ButtonEvent.POINTER_DOWN, listener);
            } else {
                this.buttons.delete(id);
                button.off(ButtonEvent.POINTER_DOWN, listener);
            }
        }
    }

    private hideHistoryButton() {
        const button = Layers.Menus.getFlexiButton("history");
        if(button) {
            button.setEnabled(false);
            button.setVisible(false);
        }
    }

    private hideAutoplayButton() {
        const button = Layers.Menus.getFlexiButton("autoplay");
        if(button) {
            button.setEnabled(false);
            button.setVisible(false);
        }
    }

    private enableClose(enabled: boolean) {
        const closeBtn = Layers.Menus.getFlexiButton("close");
        if (closeBtn) {
            closeBtn.setEnabled(enabled);
            Layers.Menus.getFlexiButton("close").setVisible(enabled);
        }
    }

    private setup() {
        // Footer texts
        this.balanceText = Layers.Menus.getFlexiText("balance_value");
        this.stakeText = Layers.Menus.getFlexiText("total_stake_value");
        this.winText = Layers.Menus.getFlexiText("total_win_value");

        // Home button
        this.homeButton = Layers.Menus.getFlexiButton("home");
        if (this.homeButton) {
            if (this.homeURL === "close" || this.homeURL === "" || this.homeURL === undefined || this.homeURL === null)  {
                this.homeButton.setVisible(false);
            } else {
                this.homeButton.setVisible(true);
            }
        }
        if (this.homeButton) {
            this.homeButton.on(ButtonEvent.POINTER_UP, this.homePressedHandler);
        }

        // Sound
        const soundOnButton = Layers.Menus.getFlexiButton("soundOn");
        const soundOffButton = Layers.Menus.getFlexiButton("soundOff");
        const soundLoadingSprite = Layers.Menus.getFlexiSprite("soundLoading");

        if (soundOnButton && soundOffButton) {
            this.soundToggle = new SoundToggleComponent(soundOnButton, soundOffButton, soundLoadingSprite);
        }

        this.updateFields();
    }

    private getSceneAlias(scene: string) {
        if (BetMenuComponent.sceneAliases.has(scene)) {
            return BetMenuComponent.sceneAliases.get(scene);
        }

        return scene;
    }

    private destroy() {
        if (this.homeButton) {
            this.homeButton.off(ButtonEvent.POINTER_UP, this.homePressedHandler);
        }
        if (this.soundToggle) {
            this.soundToggle.destroy();
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/betbar/bet-menu-component.ts